import React, { createContext, useContext } from "react";
import { FormattedMessage } from "react-intl";

import { featureFlags } from "~/common/feature-flags";
import { buttonVariants, cn } from "~/common/redesign-v1";

/** Patient create and draft-save success popup, portaled outside the legacy notification host in redesign mode. */

const RedesignPopupDismissContext = createContext<(() => void) | null>(null);

export function RedesignPopupDismissProvider({
  dismiss,
  children,
}: {
  dismiss: () => void;
  children: React.ReactNode;
}) {
  return (
    <RedesignPopupDismissContext.Provider value={dismiss}>{children}</RedesignPopupDismissContext.Provider>
  );
}

function useRedesignPopupDismiss(): (() => void) | null {
  return useContext(RedesignPopupDismissContext);
}

export function RedesignPatientActionCard({
  title,
  children,
  onOk,
  testId,
}: {
  title: React.ReactNode;
  children?: React.ReactNode;
  onOk?: () => void;
  testId?: string;
}) {
  const dismissPortal = useRedesignPopupDismiss();

  return (
    <div
      data-redesign-v1
      data-testid={testId}
      className={cn(
        // The redesign MODAL SHELL (frame 5068-7388, the one `common/redesign-v1/ui/alert-dialog.tsx` is
        // matched to): 32px padding, 12px radius, `border`/`background` tokens, `shadow-lg`, 16px gaps,
        // centred column, capped at `max-w-sm` and inset by 2rem on a phone.
        "tw-box-border tw-flex tw-w-[calc(100%-2rem)] tw-max-w-sm tw-flex-col tw-gap-4",
        "tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-background",
        "tw-p-8 tw-font-sans tw-text-foreground tw-shadow-lg",
      )}
    >
      {/* paragraph-small on `muted-foreground`, centred — the shell's body style. This popup is a plain
          acknowledgement, so the message IS the body; there is no separate heading to invent. */}
      <div className="tw-text-center tw-text-sm tw-font-normal tw-leading-5 tw-text-muted-foreground">
        <div>{title}</div>
        {children != null && children !== false ? <div className="tw-mt-1">{children}</div> : null}
      </div>
      {/* Full-width primary, like the frame's button group — was a 28px uppercase pill floating to the
          right of the text, which is the legacy `wpb-popup--small` bar shape, not this design's. */}
      <button
        type="button"
        className={cn(buttonVariants({ variant: "default" }), "tw-w-full")}
        onClick={() => {
          onOk?.();
          dismissPortal?.();
        }}
      >
        <FormattedMessage id="notify.ok" defaultMessage="OK" />
      </button>
    </div>
  );
}

export function shouldUseRedesignPatientNotification(): boolean {
  return featureFlags.redesignV1;
}
